import { EventEmitter, Injectable } from '@angular/core';
import { Tab } from 'src/app/model/common/tab';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { SharedData } from 'src/app/model/controllerNotes/sharedData';

@Injectable({
  providedIn: 'root',
})
export class TabService {
  tabsChangedEvent = new EventEmitter<Tab[]>();
  attemptCloseEvent = new EventEmitter<any>();
  revertTabChangeEvent = new EventEmitter<Tab>();
  mobileTabChanged = new EventEmitter<Tab>();
  activeTabChanged = new EventEmitter<Tab>();

  private openTabs: Tab[] = [];
  private prevTabs: Tab[] = [];
  private canClose: boolean = false;
  private mobileTab!: Tab;

  openTab(tab: Tab | null, newTab = false): void {
    if (tab) {
      if (newTab) {
        this.newTab(tab);
      } else if (!tab.id) {
        if (
          !this.openTabs.find((t) => t.container === tab.container && !t.id)
        ) {
          this.newTab(tab);
        } else {
          this.resetSelection();
          this.openTabs.find(
            (t) => t.container === tab.container && isNullOrUndefined(t.id)
          )!.selected = true;
          this.tabsChangedEvent.emit(this.openTabs);
          this.activeTabChanged.emit(
            this.openTabs.find(
              (t) => t.container === tab.container && isNullOrUndefined(t.id)
            )
          );
        }
      } else if (tab.id) {
        if (
          !this.openTabs.find(
            (t) => t.id === tab.id && t.container === tab.container
          )
        ) {
          this.newTab(tab);
        } else {
          this.resetSelection();
          this.openTabs.find((t) => t.id === tab.id)!.selected = true;
          this.tabsChangedEvent.emit(this.openTabs);
          this.activeTabChanged.emit(
            this.openTabs.find((t) => t.id === tab.id)
          );
        }
      }
    }
  }

  private newTab(tab: Tab): void {
    this.prevTabs.push(this.openTabs.find((t) => t.selected === true)!);
    this.resetSelection();
    this.openTabs = [...this.openTabs, tab];
    this.tabsChangedEvent.emit(this.openTabs);
    this.activeTabChanged.emit(tab);
  }

  replaceTab(oldTab: Tab, newTab: Tab): void {
    if (
      !isNullOrUndefined(newTab) &&
      !isNullOrUndefined(oldTab) &&
      !isNullOrUndefined(this.openTabs) &&
      this.openTab.length > 0
    ) {
      const index = oldTab.index;
      newTab.index = index;
      this.openTabs[index] = newTab;
      this.openTabs = [...this.openTabs];
      this.tabsChangedEvent.emit(this.openTabs);
    }
  }

  closeTab(index: number, closeAll = false): void {
    if (
      (!isNullOrUndefined(this.openTabs) &&
        this.openTabs.length > 0 &&
        !isNullOrUndefined(index) &&
        !isNullOrUndefined(this.openTabs[index])) ||
      closeAll
    ) {
      if (!closeAll) {
        this.openTabs.splice(index, 1);
      } else {
        const homeIndex = this.openTabs.findIndex((t) => t.header === 'Home');
        if (!isNullOrUndefined(homeIndex)) {
          this.openTabs.splice(homeIndex + 1);
        }
      }
      this.setIndex();
      this.openTabs = [...this.openTabs];
      this.setSelectedTab();
      this.tabsChangedEvent.emit(this.openTabs);
    }
  }

  setIndex(): void {
    let index = 0;
    this.openTabs.forEach((tab) => {
      tab.index = index++;
    });
    this.openTabs = [...this.openTabs];
  }

  buildNewTab(
    container: string,
    closable: boolean,
    header?: string,
    id?: string,
    routerLink?: string
  ): Tab {
    const retVal: Tab = {
      index: this.openTabs.length,
      header: header ? header : '',
      container: container,
      closable: closable,
      selected: true,
      id: id,
      routerLink: routerLink,
    };
    return retVal;
  }

  buildNewAORTab(
    container: string,
    closable: boolean,
    header: string,
    id: string | undefined,
    routerLink: string | undefined,
    data: SharedData
  ): Tab {
    const retVal = {
      index: this.openTabs.length,
      header: header ? header : '',
      container: container,
      closable: closable,
      selected: true,
      id: id,
      routerLink: routerLink,
      data: data,
    };

    return retVal;
  }

  getActiveTab(): Tab {
    return this.openTabs.find((t) => t.selected === true)!;
  }

  getActiveIndex(): number {
    if (!isNullOrUndefined(this.openTabs) && this.openTab.length > 0) {
      return this.openTabs.findIndex((i) => i.selected === true);
    }

    return -1;
  }

  setActiveTab(index: number) {
    this.prevTabs.push(this.openTabs.find((t) => t.selected === true)!);
    this.resetSelection();
    this.openTabs[index].selected = true;
    this.activeTabChanged.emit(this.openTabs[index]);
  }

  updateActiveTabLabel(label: string): void {
    if (label.length > 0 && this.openTabs) {
      const tab = this.openTabs.find((tab) => tab.selected);
      if (tab) {
        this.openTabs[tab.index].header = label;
      }
    }
  }

  updateTabLabel(tab: Tab, label: string): void {
    if (label.length > 0 && this.openTabs) {
      if (tab) {
        this.openTabs[tab.index].header = label;
      }
    }
  }

  /** Re-keys an open tab in place, e.g. after a new record is first saved. */
  updateTabId(oldId: string, newId: string): void {
    const tab = this.openTabs.find((t) => t.id === oldId);
    if (tab) {
      tab.id = newId;
    }
    if (this.mobileTab?.id === oldId) {
      this.mobileTab.id = newId;
    }
  }

  getTabByIndex(index: number) {
    return this.openTabs[index];
  }

  getTabById(id: string) {
    return this.openTabs.find((tab) => tab.id === id);
  }

  getTabByIdAndContainer(id: string, container: string) {
    return this.openTabs.find(
      (tab) => tab.id === id && tab.container === container
    );
  }

  revertToPrevTab(): void {
    const tab = this.prevTabs.pop();
    this.resetSelection();
    this.openTabs[tab!.index].selected = true;
    this.activeTabChanged.emit(tab);
  }

  getCanClose(): boolean {
    return this.canClose;
  }

  setCanClose(flag: boolean): void {
    this.canClose = flag;
  }

  resetSelection(): void {
    if (!isNullOrUndefined(this.openTabs)) {
      this.openTabs.forEach((tab) => {
        tab.selected = false;
      });
    }
  }

  getMobileTab(): Tab {
    return this.mobileTab;
  }

  setMobileTab(tab: Tab): void {
    this.mobileTab = tab;
    this.mobileTabChanged.emit(this.mobileTab);
  }

  setSelectedTab(): void {
    if (
      !isNullOrUndefined(this.openTabs) &&
      this.openTabs.length > 0 &&
      !isNullOrUndefined(this.prevTabs) &&
      this.prevTabs.length > 0
    ) {
      const prevTab = this.prevTabs.pop();
      if (this.openTabs.includes(prevTab!)) {
        this.resetSelection();
        this.openTabs[prevTab!.index].selected = true;
        this.activeTabChanged.emit(prevTab);
      } else {
        this.setSelectedTab();
      }
    } else {
      this.openTabs[0].selected = true;
      this.activeTabChanged.emit(this.openTabs[0]);
    }
  }
}
